<template>
	<div class="date-field-container">
		<div class="date-input-wrapper" :class="{ disabled: disabled, dark: theme === 'dark' }">
			<div class="date-input-content" :class="{ 'has-value': displayValue }">
				<span class="date-label">{{ label }} {{ required ? '*' : '' }}</span>
				<input
					type="text"
					class="date-input"
					:value="displayValue"
					@input="handleTextInput"
					@blur="validateAndFormat"
					placeholder="DD/MM/YYYY"
					:disabled="disabled"
					inputmode="numeric"
					maxlength="10"
				/>
			</div>
			<div class="date-actions">
				<v-icon v-if="modelValue && clearable && !disabled" class="date-clear" @click.stop="clearValue">mdi-close</v-icon>
				<v-icon class="date-calendar" @click.stop="openDrawer" :class="{ disabled: disabled }">mdi-calendar</v-icon>
			</div>
		</div>

		<DrawerPanel
			ref="drawerPanel"
			:title="label"
			:divider="false"
			:close="true"
			openHeight="70%"
			closedHeight="0px"
			bottom="0"
			backgroundColor="#fff"
			openBackgroundColor="#fff"
			:blurBackground="true"
		>
			<div class="calendar-content">
				<div class="calendar-header">
					<v-icon class="nav-arrow" @click="previousMonth">mdi-chevron-left</v-icon>
					<div class="month-year-selector">
						<span class="current-month" @click="showMonthPicker = !showMonthPicker; showYearPicker = false">{{ monthNames[viewMonth] }}</span>
						<span class="current-year" @click="showYearPicker = !showYearPicker; showMonthPicker = false">{{ viewYear }}</span>
					</div>
					<v-icon class="nav-arrow" @click="nextMonth">mdi-chevron-right</v-icon>
				</div>

				<!-- Month Picker -->
				<div v-if="showMonthPicker" class="picker-grid month-picker">
					<div
						v-for="(month, index) in monthNames"
						:key="month"
						class="picker-item"
						:class="{ selected: index === viewMonth }"
						@click="selectMonth(index)"
					>
						{{ month.substring(0, 3) }}
					</div>
				</div>

				<!-- Year Picker -->
				<div v-if="showYearPicker" class="picker-grid year-picker">
					<div
						v-for="year in yearRange"
						:key="year"
						class="picker-item"
						:class="{ selected: year === viewYear }"
						@click="selectYear(year)"
					>
						{{ year }}
					</div>
				</div>

				<!-- Calendar Grid -->
				<div v-if="!showMonthPicker && !showYearPicker" class="calendar-grid">
					<div class="weekday-header">
						<span v-for="day in weekdays" :key="day" class="weekday">{{ day }}</span>
					</div>
					<div class="days-grid">
						<div
							v-for="(day, index) in calendarDays"
							:key="index"
							class="day-cell"
							:class="{
								'other-month': day.otherMonth,
								'selected': isSelected(day),
								'today': isToday(day)
							}"
							@click="selectDate(day)"
						>
							{{ day.date }}
						</div>
					</div>
				</div>

				<div class="calendar-footer">
					<button class="today-button" @click="goToToday">Today</button>
				</div>
			</div>
		</DrawerPanel>
	</div>
</template>

<script>
import DrawerPanel from './DrawerPanel.vue';

export default {
	name: 'DateFieldDrawer',
	components: { DrawerPanel },
	props: {
		modelValue: {
			type: [String, Date],
			default: null
		},
		label: {
			type: String,
			required: true
		},
		required: {
			type: Boolean,
			default: false
		},
		clearable: {
			type: Boolean,
			default: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		theme: {
			type: String,
			default: null
		}
	},
	emits: ['update:modelValue'],
	data() {
		const today = new Date();
		return {
			viewMonth: today.getMonth(),
			viewYear: today.getFullYear(),
			showMonthPicker: false,
			showYearPicker: false,
			weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
			monthNames: [
				'January', 'February', 'March', 'April', 'May', 'June',
				'July', 'August', 'September', 'October', 'November', 'December'
			],
			textValue: ''
		};
	},
	computed: {
		displayValue() {
			if (!this.modelValue) return '';
			if (typeof this.modelValue === 'string') {
				return this.modelValue;
			}
			if (this.modelValue instanceof Date) {
				return this.formatDate(this.modelValue);
			}
			return '';
		},
		selectedDate() {
			if (!this.modelValue) return null;
			if (this.modelValue instanceof Date) return this.modelValue;
			if (typeof this.modelValue === 'string') {
				return this.parseDate(this.modelValue);
			}
			return null;
		},
		calendarDays() {
			const days = [];
			const firstDay = new Date(this.viewYear, this.viewMonth, 1);
			const lastDay = new Date(this.viewYear, this.viewMonth + 1, 0);

			// Get the day of week for the first day (0 = Sunday, adjust for Monday start)
			let startDay = firstDay.getDay() - 1;
			if (startDay < 0) startDay = 6;

			// Add days from previous month
			const prevMonth = new Date(this.viewYear, this.viewMonth, 0);
			for (let i = startDay - 1; i >= 0; i--) {
				days.push({
					date: prevMonth.getDate() - i,
					month: this.viewMonth - 1,
					year: this.viewMonth === 0 ? this.viewYear - 1 : this.viewYear,
					otherMonth: true
				});
			}

			// Add days from current month
			for (let i = 1; i <= lastDay.getDate(); i++) {
				days.push({
					date: i,
					month: this.viewMonth,
					year: this.viewYear,
					otherMonth: false
				});
			}

			// Add days from next month to complete the grid
			const remaining = 42 - days.length;
			for (let i = 1; i <= remaining; i++) {
				days.push({
					date: i,
					month: this.viewMonth + 1,
					year: this.viewMonth === 11 ? this.viewYear + 1 : this.viewYear,
					otherMonth: true
				});
			}

			return days;
		},
		yearRange() {
			const currentYear = new Date().getFullYear();
			const years = [];
			for (let i = currentYear - 100; i <= currentYear + 10; i++) {
				years.push(i);
			}
			return years;
		}
	},
	methods: {
		formatDate(date) {
			if (!date) return '';
			const d = date.getDate().toString().padStart(2, '0');
			const m = (date.getMonth() + 1).toString().padStart(2, '0');
			const y = date.getFullYear();
			return `${d}/${m}/${y}`;
		},
		parseDate(str) {
			if (!str) return null;
			const parts = str.split('/');
			if (parts.length !== 3) return null;
			const day = parseInt(parts[0], 10);
			const month = parseInt(parts[1], 10) - 1;
			const year = parseInt(parts[2], 10);
			if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
			return new Date(year, month, day);
		},
		handleTextInput(event) {
			const isDelete = event.inputType && event.inputType.startsWith('delete');
			let value = event.target.value;

			if (isDelete) {
				// On delete, just remove invalid characters but don't reformat
				value = value.replace(/[^\d/]/g, '');
			} else {
				// On insert, strip to digits and reformat with slashes
				value = value.replace(/[^\d]/g, '');
				if (value.length >= 2) {
					value = value.substring(0, 2) + '/' + value.substring(2);
				}
				if (value.length >= 5) {
					value = value.substring(0, 5) + '/' + value.substring(5, 9);
				}
			}

			event.target.value = value;

			if (value.length === 10) {
				this.validateAndFormat({ target: { value } });
			}
		},
		validateAndFormat(event) {
			const value = event.target.value;
			if (!value) {
				this.$emit('update:modelValue', null);
				return;
			}

			const date = this.parseDate(value);
			if (date && !isNaN(date.getTime())) {
				this.$emit('update:modelValue', this.formatDate(date));
			}
		},
		openDrawer() {
			if (this.disabled) return;

			// Set view to selected date or today
			if (this.selectedDate) {
				this.viewMonth = this.selectedDate.getMonth();
				this.viewYear = this.selectedDate.getFullYear();
			} else {
				const today = new Date();
				this.viewMonth = today.getMonth();
				this.viewYear = today.getFullYear();
			}

			this.showMonthPicker = false;
			this.showYearPicker = false;
			this.$refs.drawerPanel.openDrawer();
		},
		clearValue() {
			this.$emit('update:modelValue', null);
		},
		previousMonth() {
			if (this.viewMonth === 0) {
				this.viewMonth = 11;
				this.viewYear--;
			} else {
				this.viewMonth--;
			}
		},
		nextMonth() {
			if (this.viewMonth === 11) {
				this.viewMonth = 0;
				this.viewYear++;
			} else {
				this.viewMonth++;
			}
		},
		selectMonth(index) {
			this.viewMonth = index;
			this.showMonthPicker = false;
		},
		selectYear(year) {
			this.viewYear = year;
			this.showYearPicker = false;
		},
		selectDate(day) {
			const date = new Date(
				day.month < 0 ? day.year : (day.month > 11 ? day.year : this.viewYear),
				day.month < 0 ? 11 : (day.month > 11 ? 0 : day.month),
				day.date
			);
			this.$emit('update:modelValue', this.formatDate(date));

			setTimeout(() => {
				this.$refs.drawerPanel.closeDrawer();
			}, 150);
		},
		goToToday() {
			const today = new Date();
			this.viewMonth = today.getMonth();
			this.viewYear = today.getFullYear();
			this.$emit('update:modelValue', this.formatDate(today));

			setTimeout(() => {
				this.$refs.drawerPanel.closeDrawer();
			}, 150);
		},
		isSelected(day) {
			if (!this.selectedDate) return false;
			return (
				day.date === this.selectedDate.getDate() &&
				day.month === this.selectedDate.getMonth() &&
				(day.otherMonth ? true : day.year === this.selectedDate.getFullYear())
			);
		},
		isToday(day) {
			const today = new Date();
			return (
				day.date === today.getDate() &&
				day.month === today.getMonth() &&
				!day.otherMonth &&
				this.viewYear === today.getFullYear()
			);
		}
	}
};
</script>

<style scoped>
.date-field-container {
	margin-bottom: 16px;
}

.date-input-wrapper {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px;
	background-color: #3328470D;
	border-radius: 8px;
	min-height: 56px;
	transition: background-color 0.2s;
}

.date-input-wrapper:hover:not(.disabled) {
	background-color: #33284715;
}

.date-input-wrapper.disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.date-input-content {
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1;
}

.date-label {
	font-size: 12px;
	color: #666;
	transition: font-size 0.2s;
}

.date-input-content.has-value .date-label {
	font-size: 11px;
}

.date-input {
	border: none;
	background: transparent;
	font-size: 16px;
	color: #413655;
	padding: 0;
	outline: none;
	width: 100%;
	font-family: inherit;
}

.date-input::placeholder {
	color: #999;
}

.date-input:disabled {
	cursor: not-allowed;
}

.date-actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.date-clear,
.date-calendar {
	color: #666;
	cursor: pointer;
}

.date-clear:hover,
.date-calendar:hover:not(.disabled) {
	color: #413655;
}

.date-calendar.disabled {
	cursor: not-allowed;
	opacity: 0.5;
}

/* Calendar styles */
.calendar-content {
	padding: 8px 0;
}

.calendar-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 0 16px 0;
}

.nav-arrow {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	cursor: pointer;
	color: #413655;
	transition: background-color 0.2s;
}

.nav-arrow:hover {
	background-color: #3328470D;
}

.month-year-selector {
	display: flex;
	gap: 8px;
	font-size: 18px;
	font-weight: 600;
	color: #413655;
}

.current-month,
.current-year {
	cursor: pointer;
	padding: 4px 8px;
	border-radius: 4px;
	transition: background-color 0.2s;
}

.current-month:hover,
.current-year:hover {
	background-color: #3328470D;
}

/* Picker grids */
.picker-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 8px;
	padding: 16px 0;
}

.year-picker {
	grid-template-columns: repeat(4, 1fr);
	max-height: 280px;
	overflow-y: auto;
}

.picker-item {
	padding: 12px;
	text-align: center;
	border-radius: 8px;
	cursor: pointer;
	font-size: 14px;
	color: #413655;
	transition: background-color 0.2s;
}

.picker-item:hover {
	background-color: #3328470D;
}

.picker-item.selected {
	background-color: #413655;
	color: #fff;
}

/* Calendar grid */
.calendar-grid {
	margin-top: 8px;
}

.weekday-header {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	margin-bottom: 8px;
}

.weekday {
	text-align: center;
	font-size: 12px;
	font-weight: 600;
	color: #999;
	padding: 8px 0;
}

.days-grid {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	gap: 4px;
}

.day-cell {
	aspect-ratio: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	cursor: pointer;
	font-size: 14px;
	color: #413655;
	transition: all 0.2s;
}

.day-cell:hover {
	background-color: #3328470D;
}

.day-cell.other-month {
	color: #ccc;
}

.day-cell.today {
	border: 2px solid #413655;
}

.day-cell.selected {
	background-color: #413655;
	color: #fff;
}

.day-cell.selected.today {
	border-color: #413655;
}

/* Footer */
.calendar-footer {
	display: flex;
	justify-content: center;
	padding-top: 16px;
	border-top: 1px solid #3328470D;
	margin-top: 16px;
}

.today-button {
	padding: 10px 24px;
	background-color: #3328470D;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 600;
	color: #413655;
	cursor: pointer;
	transition: background-color 0.2s;
}

.today-button:hover {
	background-color: #33284720;
}

/* Dark theme overrides */
.date-input-wrapper.dark,
.date-input-wrapper.dark:hover:not(.disabled) {
	background-color: #ffffffaa;
}

.date-input-wrapper.dark .date-label {
	color: #333;
}

.date-input-wrapper.dark .date-input {
	color: #333;
}

.date-input-wrapper.dark .date-input::placeholder {
	color: #666;
}

.date-input-wrapper.dark .date-clear,
.date-input-wrapper.dark .date-calendar {
	color: #555;
}
</style>
